import React from "react";
import Modal from "react-modal";
import { Oval } from "react-loader-spinner";
import toast from "react-hot-toast";

import "./PhotoThumb.scss";

interface Props {
  // Visible label under the icon, e.g. "View photo" / "View receipt".
  label: string;
  // "receipt" tints the thumbnail like paper; "photo" is the default image look.
  kind?: "photo" | "receipt";
  // Title shown in the modal header.
  modalTitle: string;
  // Lazily fetches the base64 image body (no data: prefix) on first open.
  fetchImage: () => Promise<string>;
}

// A click-to-view image thumbnail that lazy-loads its image only on first open
// and caches it for re-opens. Used for the delivery photo and the dump receipt
// in the timeline so neither is fetched until the admin actually asks for it.
export function PhotoThumb({ label, kind = "photo", modalTitle, fetchImage }: Props) {
  const [open, setOpen] = React.useState(false);
  const [dataUrl, setDataUrl] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [zoomed, setZoomed] = React.useState(false);

  const close = () => {
    setOpen(false);
    setZoomed(false);
  };

  const onOpen = async () => {
    setOpen(true);
    setZoomed(false);
    if (dataUrl) return;
    setLoading(true);
    try {
      const raw = await fetchImage();
      // Fetchers are inconsistent: some return raw base64, some already return a
      // full `data:` URL. Only prefix when it isn't already a data URL.
      setDataUrl(raw.startsWith("data:") ? raw : `data:image/jpeg;base64,${raw}`);
    } catch {
      toast.error("Failed to load image.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className={`photo-thumb ${kind}`}
        onClick={onOpen}
        title={modalTitle}
      >
        <span className="icon">{kind === "receipt" ? "🧾" : "🖼"}</span>
        <span className="lbl">{label}</span>
      </button>

      <Modal
        isOpen={open}
        onRequestClose={close}
        className="photo-viewer-modal"
        overlayClassName="photo-viewer-overlay"
        style={{ overlay: { zIndex: 1000 } }}
      >
        <div className="photo-viewer">
          <div className="pv-head">
            <div className="pv-title">{modalTitle}</div>
            <div className="pv-controls">
              {dataUrl && !loading && (
                <button
                  type="button"
                  className="pv-btn"
                  aria-label={zoomed ? "Zoom out" : "Zoom in"}
                  onClick={() => setZoomed((z) => !z)}
                >
                  {zoomed ? "➖ Zoom out" : "➕ Zoom in"}
                </button>
              )}
              <button
                type="button"
                className="pv-btn close"
                aria-label="Close image viewer"
                onClick={close}
              >
                ✕
              </button>
            </div>
          </div>

          <div className="pv-stage">
            {loading && (
              <div className="pv-loader">
                <Oval color="#131313" height={44} width={44} secondaryColor="#FBD20A" />
              </div>
            )}
            {!loading && dataUrl && (
              <img
                src={dataUrl}
                alt={modalTitle}
                className={`pv-img ${zoomed ? "zoomed" : ""}`}
                title={zoomed ? "Click to zoom out" : "Click to zoom in"}
                onClick={() => setZoomed((z) => !z)}
              />
            )}
          </div>
        </div>
      </Modal>
    </>
  );
}
